import { z } from 'zod';
import { customerTypeSchema } from '../master-data.js';
import { taxScenarioSchema } from '../documents.js';
import { ustStatusSchema } from '../einstellungen.js';
/**
 * Der Vertrag der Szenario-Ableitung — Migration `0177`, Chip `ST-ort`.
 *
 * Spec `docs/superpowers/specs/2026-09-06-steuerlogik-design.md`, Abschnitt
 * 4.2 (`D-9`, `D-10`, `D-13`, `D-14`, `D-16`), Plan
 * `docs/superpowers/plans/2026-09-06-steuerlogik-umsetzung.md`, Task 2.
 *
 * **Die Ableitung ist eine benannte Funktion mit einer Tabelle dahinter und
 * ausdruecklich keine Regel** (`D-10`). § 3a Abs. 2 UStG traegt die
 * Unternehmerzeilen (Empfaengerortprinzip), § 3a Abs. 5 UStG die
 * Nichtunternehmerzeilen (Wohnsitzort bei elektronisch erbrachten sonstigen
 * Leistungen). Wer sie konfigurierbar macht, nimmt die Regel-Engine-Aera
 * vorweg, die er nicht entworfen hat.
 *
 * **Die Eingabe ist vollstaendig, und das ist der Punkt.** Die Ableitung
 * liest nichts selbst — weder die Mitgliedschaft, noch den Pruefstand der
 * USt-IdNr., noch den Stand des OSS-Schalters. Alle drei stehen in der
 * Eingabe, weil eine reine Funktion die einzige Form ist, in der sich die
 * sieben Zeilen der Tabelle aus Spec 4.2 einzeln beweisen lassen: Ein Modul,
 * das seine eigenen Vorbedingungen holt, braucht fuer jede Zeile eine
 * Datenbank.
 *
 * **Es gibt keinen Schreibvertrag.** Diese Datei beschreibt eine Rechnung
 * und keinen Weg, an dem etwas gespeichert wuerde. Das UEBERSCHREIBEN des
 * abgeleiteten Szenarios (`D-11`: Faehigkeit `steuer:szenario`,
 * Pflichtbegruendung, `tax_scenario_source = 'declared'`) gehoert zu Task 8
 * [`ST-dienst`] und steht deshalb nicht hier.
 */
/**
 * Der Pruefstand der USt-IdNr. des Empfaengers zum **Leistungsstichtag**.
 *
 * Vier Werte, weil die Ableitung nur einen davon traegt und die drei anderen
 * verschiedene Geschichten erzaehlen, die ein Mensch auseinanderhalten muss:
 *
 * - `gueltig` — eine aufgezeichnete Pruefung mit `result = 'valid'`, deren
 *   `checked_at` innerhalb der Frist vor dem Leistungsstichtag liegt.
 * - `ungueltig` — die juengste Pruefung sagt `invalid` oder `unavailable`.
 * - `abgelaufen` — es gab eine gueltige Pruefung, aber sie liegt weiter
 *   zurueck als die Frist.
 * - `fehlt` — es ist ueberhaupt keine Pruefung aufgezeichnet.
 *
 * **Berechnet wird der Wert nicht hier.** Die Abfrage „juengste Pruefung je
 * Kunde" und die Frist als **eine** Konstante (`D-16b`: zwoelf Monate)
 * gehoeren zu Task 5 [`ST-ustid`]; diese Ableitung verbraucht das Ergebnis.
 * Zwei Orte, an denen die Frist steht, waeren ein Ort zu viel.
 */
export const ustIdPruefstandSchema = z.enum(['gueltig', 'ungueltig', 'abgelaufen', 'fehlt']);
/**
 * Welchen Satz das abgeleitete Szenario verlangt.
 *
 * **Ausdruecklich NICHT `SteuersatzArt`**, und das ist die einzige nicht
 * offensichtliche Entscheidung dieser Datei. Ob ein Umsatz dem Regelsatz oder
 * dem ermaessigten Satz unterliegt, folgt aus der **Leistung** (§ 12 Abs. 2
 * UStG, die Anlage 2 zum UStG) und nicht aus dem **Ort**. Der Ort entscheidet
 * nur, ob ueberhaupt deutsche Umsatzsteuer anfaellt. Wer hier `'regel'`
 * zurueckgaebe, behauptete eine Aussage ueber den Leistungsgegenstand, die
 * diese Funktion nicht treffen kann — und der naechste Leser haelte sie fuer
 * geprueft.
 *
 * - `inlaendisch` — deutscher Satz; welcher (19 % oder 7 %) entscheidet die
 *   Position, aufgeloest ueber `steuersatz` am Leistungsstichtag.
 * - `null_prozent` — 0 %, und zwar als eigene Satzart aus `0176` und nicht
 *   als ermaessigter Satz von null.
 */
export const satzvorgabeSchema = z.enum(['inlaendisch', 'null_prozent']);
/**
 * Warum ein Szenario herauskam — je Zeile der Tabelle aus Spec 4.2 einer.
 *
 * Ein Code und kein Satz: Der Grund wird angezeigt, protokolliert und im
 * Befundbericht gruppiert (Task 10). Ein deutscher Satz waere an allen drei
 * Stellen dieselbe Zeichenkette, die niemand mehr umformulieren darf.
 */
export const szenarioGrundSchema = z.enum([
    /** Empfaenger im Inland. Kundentyp und USt-IdNr. spielen keine Rolle. */
    'inland',
    /** EU, Unternehmer, gueltige und nicht abgelaufene Pruefung. */
    'eu_b2b_mit_gepruefter_ustid',
    /** EU, Nichtunternehmer, OSS-Schalter aus — deutsche Steuer bleibt richtig. */
    'eu_b2c_unter_schwelle',
    /** Drittland, Unternehmer. */
    'drittland_b2b',
    /** Drittland, Nichtunternehmer. */
    'drittland_b2c',
    /**
     * Inland oder EU-Nichtunternehmer unter der Schwelle, und die Steuerfassung
     * am Leistungsstichtag traegt den Status `kleinunternehmer` — keine
     * Umsatzsteuer nach § 19 Abs. 1 UStG (Migration `0259`, `PF-kuszenario`).
     */
    'kleinunternehmer',
]);
/**
 * Warum **kein** Szenario herauskam.
 *
 * Die Werte sind in der Form der uebrigen Fehlercodes dieses Systems
 * geschrieben, weil sie an der Ausstellung als solcher herauskommen und nicht
 * nur intern gelesen werden.
 *
 * - `TAX_SCENARIO_EU_B2B_VAT_ID_REQUIRED` (`D-14`) — die **siebte Zeile** der
 *   Tabelle aus Spec 4.2. EU + b2b ohne geprueft gueltige USt-IdNr. leitet
 *   nichts ab, und die Ausstellung wird abgewiesen. **Kein stiller Wechsel
 *   auf 19 %**: Reverse Charge ist der einzige Fall der Tabelle, dessen
 *   Steuerfreiheit an einer pruefbaren Eigenschaft des Empfaengers haengt,
 *   und das Umschalten zwischen zwei Steuerbildern ohne Zutun eines Menschen
 *   ist genau der Fehler, den die 139 gemessenen Altbelege zeigen. § 14a
 *   Abs. 1 UStG verlangt die USt-IdNr. beider Seiten ohnehin.
 * - `TAX_SCENARIO_OSS_NOT_BUILT` (`D-13`) — der OSS-Schalter steht an, aber
 *   das Verfahren ist nicht gebaut: keine Saetze des Kundenlandes in
 *   `steuersatz`, kein Meldeformat, keine Erloeskonten je Bestimmungsland.
 *   Ein `eu_b2c_oss`, das diese Funktion ableitete, waere ein Szenario ohne
 *   Satz.
 */
export const szenarioAblehnungSchema = z.enum([
    'TAX_SCENARIO_EU_B2B_VAT_ID_REQUIRED',
    'TAX_SCENARIO_OSS_NOT_BUILT',
]);
/**
 * Ein Datum in ISO-Form, so wie es aus `date`-Spalten kommt.
 *
 * Dieselbe Zeile wie in `quelle.ts`, und aus demselben Grund eine eigene:
 * damit ein vertauschtes Feld hier auffaellt und nicht erst beim Vergleichen
 * zweier Daten.
 */
const isoDatumSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet wird ein Datum JJJJ-MM-TT.');
/**
 * Die Eingabe der Ableitung.
 *
 * **`stichtag` ist der Leistungsstichtag**, nie das Rechnungs- und nie das
 * Buchungsdatum — dieselbe Warnung wie in
 * `apps/api/src/steuer/satz-aufloesung.ts`. Er steht hier, obwohl die reine
 * Ableitung ihn nicht mehr befragt: Die Mitgliedschaft ist bereits an IHM
 * aufgeloest worden (`euMitgliedAmStichtag`), und ein Ergebnis, das seinen
 * eigenen Stichtag nicht mitfuehrt, laesst sich weder protokollieren noch
 * gegen einen zweiten Aufruf halten.
 *
 * **`euMitgliedAmStichtag` ist eine Antwort und keine Frage.** Sie kommt aus
 * `eu_mitglied_am(code, datum)` (Migration `0177`) und ist deshalb ein
 * `boolean` und nicht der Laendercode allein: Waere das Land die Eingabe,
 * muesste die Funktion die Tabelle lesen und waere nicht mehr rein.
 */
export const szenarioEingabeSchema = z
    .object({
    /** Land der Rechnungsanschrift des Empfaengers, ISO-3166-1 alpha-2. */
    land: z.string().length(2),
    kundentyp: customerTypeSchema,
    /** Ergebnis von `eu_mitglied_am(land, stichtag)` aus Migration `0177`. */
    euMitgliedAmStichtag: z.boolean(),
    ustIdPruefstand: ustIdPruefstandSchema,
    /** Der **Leistungsstichtag**, nie das Buchungsdatum. */
    stichtag: isoDatumSchema,
    /** Der OSS-Schalter (`D-13`). Vorgabe **aus**; Task 8 baut ihn. */
    ossSchalter: z.boolean(),
    /**
     * Der Umsatzsteuerstatus der Steuerfassung am **Leistungsstichtag**
     * (`steuerprofil.ust_status`, Spec Systemeinstellungen 3.2, `D-12`) —
     * eine Antwort wie `euMitgliedAmStichtag` und keine Frage: Die reine
     * Ableitung liest die Fassung nicht selbst. Pflicht und ohne Vorgabe:
     * Ein still angenommenes `regel` waere fuer einen Kleinunternehmer genau
     * die falsche Voreinstellung.
     */
    ustStatus: ustStatusSchema,
})
    .strict();
/**
 * Das Ergebnis der Ableitung — eine unterschiedene Vereinigung und kein
 * Objekt mit lauter `null`-baren Feldern.
 *
 * Der Unterschied ist nicht kosmetisch: `abgeleitet: false` heisst, dass die
 * Ausstellung abzuweisen ist, und ein Aufrufer, der `szenario ?? 'domestic'`
 * schreiben koennte, haette den stillen Wechsel auf 19 % wieder eingebaut,
 * den `D-14` verbietet. In dieser Form gibt es kein `szenario`-Feld, das man
 * mit `??` auffuellen koennte.
 */
export const szenarioErgebnisSchema = z.discriminatedUnion('abgeleitet', [
    z
        .object({
        abgeleitet: z.literal(true),
        szenario: taxScenarioSchema,
        satzvorgabe: satzvorgabeSchema,
        grund: szenarioGrundSchema,
        /** Die Norm, aus der die Zeile folgt — Prosa fuer den Leser, kein Schluessel. */
        norm: z.string().min(1),
    })
        .strict(),
    z
        .object({
        abgeleitet: z.literal(false),
        ablehnung: szenarioAblehnungSchema,
        norm: z.string().min(1),
    })
        .strict(),
]);
//# sourceMappingURL=szenario.js.map